import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { Upload as UploadIcon, CheckCircle, FileText, Image, Video, Music, Palette, Shield, AlertCircle, Lock, Eye, EyeOff, Zap, Star, Globe, X, RefreshCw } from 'lucide-react'
import { formatFileSize } from '@/lib/utils'
import { uploadService, UploadOptions } from '@/services/uploadService'
import { compressionService } from '@/services/compressionService'
import { validateFileTypeAndExtension, getFileFormatErrorMessage } from '@/lib/media-utils'
import MediaPreview from '@/components/ui/MediaPreview'
import CosmicCard from '@/components/ui/CosmicCard'
import CosmicButton from '@/components/ui/CosmicButton'
import CosmicInput, { CosmicTextarea } from '@/components/ui/CosmicInput'
import { motion } from 'framer-motion'
import { gameUIVariants } from '@/lib/animations'
import toast from 'react-hot-toast'

const Upload: React.FC = () => {
  const navigate = useNavigate()
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStage, setUploadStage] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileValidation, setFileValidation] = useState<{ isValid: boolean; error?: string } | null>(null)
  const [compressionInfo, setCompressionInfo] = useState<{
    willCompress: boolean;
    estimatedSavings: number;
    quality: 'LOW' | 'MEDIUM' | 'HIGH';
  } | null>(null)
  const [uploadOptions, setUploadOptions] = useState<UploadOptions>({
    title: '',
    description: '',
    tags: [],
    category: '',
    isPublic: false
  })

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      console.log('File selected:', file.name, file.type, file.size)
      setSelectedFile(file)
      setFileValidation(null)
      
      // Enhanced file type validation with metadata and extension checks
      const typeValidation = validateFileTypeAndExtension(file)
      console.log('Type validation result:', typeValidation)
      
      if (!typeValidation.isValid) {
        const errorMessage = getFileFormatErrorMessage(file)
        setFileValidation({ isValid: false, error: errorMessage })
        toast.error(errorMessage.split('\n')[0]) // Show first line in toast
        return
      }

      // Show format compatibility info
      if (typeValidation.suggestedCorrection) {
        toast.error(`Format warning: ${typeValidation.suggestedCorrection}`)
        return
      }

      // Validate file for DRM compatibility
      try {
        console.log('Starting DRM validation...')
        const validation = await uploadService.validateFileForDRM(file)
        console.log('DRM validation result:', validation)
        setFileValidation(validation)
        
        if (!validation.isValid) {
          toast.error(validation.error || 'File validation failed')
        } else {
          toast.success(`File validated successfully for DRM protection (${typeValidation.detectedType} format)`)
          
          // Check compression eligibility
          const willCompress = compressionService.shouldCompress(file)
          const estimatedSavings = compressionService.getEstimatedSavings(file)
          
          setCompressionInfo({
            willCompress,
            estimatedSavings,
            quality: 'LOW'
          })
          
          if (willCompress) {
            toast.success(`File will be compressed (~${estimatedSavings}% size reduction)`)
          }
        }
      } catch (error) {
        console.error('Validation error:', error)
        const errorMessage = error instanceof Error ? error.message : 'File validation failed'
        setFileValidation({ isValid: false, error: errorMessage })
        toast.error(errorMessage)
      }
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      // Audio formats
      'audio/mp3': ['.mp3'],
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav'],
      'audio/wave': ['.wav'],
      'audio/flac': ['.flac'],
      'audio/aac': ['.aac'],
      'audio/m4a': ['.m4a'],
      'audio/ogg': ['.ogg'],
      'audio/webm': ['.webm'],
      // Video formats
      'video/mp4': ['.mp4'],
      'video/avi': ['.avi'],
      'video/mov': ['.mov'],
      'video/quicktime': ['.mov', '.qt'],
      'video/webm': ['.webm'],
      'video/x-matroska': ['.mkv'],
      // Image formats
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/gif': ['.gif']
    },
    maxSize: 100 * 1024 * 1024, // 100MB
    multiple: false
  })

  const handleUpload = async () => {
    if (!selectedFile || !fileValidation?.isValid) return

    setUploading(true)
    setUploadProgress(0)
    setUploadStage('Starting upload...')

    try {
      const result = await uploadService.uploadContent(
        selectedFile,
        uploadOptions,
        (progress) => {
          setUploadProgress(progress)
          // Update stage based on progress
          if (progress < 30) {
            setUploadStage('Compressing file...')
          } else if (progress < 60) {
            setUploadStage('Applying DRM protection...')
          } else if (progress < 90) {
            setUploadStage('Uploading to secure storage...')
          } else {
            setUploadStage('Finalizing upload...')
          }
        }
      )
      
      console.log('Upload result received:', result)
      
      // Show success message with compression results if available
      if (result.compressionResult) {
        const { compressionRatio, originalSize, compressedSize } = result.compressionResult
        toast.success(
          `Upload completed! Compression saved ${compressionRatio.toFixed(1)}% ` +
          `(${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)}). Redirecting to record page...`
        )
      } else {
        toast.success('Content uploaded successfully with DRM protection! Redirecting to record page...')
      }
      
      // Redirect to the record page if redirectTo is provided
      if (result.redirectTo) {
        setTimeout(() => {
          navigate(result.redirectTo)
        }, 2000) // Give user time to see the success message
      } else if (result.recordId) {
        setTimeout(() => {
          navigate(`/media/${result.recordId}`)
        }, 2000)
      }
      
      resetForm()
    } catch (error: any) {
      toast.error(error.message || 'Upload failed')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const resetForm = () => {
    setSelectedFile(null)
    setFileValidation(null)
    setCompressionInfo(null)
    setUploadStage('')
    setUploadOptions({
      title: '',
      description: '',
      tags: [],
      category: '',
      isPublic: false
    })
  }

  const handleChangeFile = () => {
    setSelectedFile(null)
    setFileValidation(null)
    setCompressionInfo(null)
  }

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return Image
    if (file.type.startsWith('video/')) return Video
    if (file.type.startsWith('audio/')) return Music
    return FileText
  }

  const getFileType = (file: File) => {
    if (file.type.startsWith('image/')) return 'Image'
    if (file.type.startsWith('video/')) return 'Video'
    if (file.type.startsWith('audio/')) return 'Audio'
    return 'File'
  }

  const getDRMStatus = () => {
    if (!selectedFile) return null
    
    if (fileValidation?.isValid) {
      return (
        <div className="flex items-center text-stellar-cyan">
          <Shield className="h-4 w-4 mr-2" />
          <span className="text-sm font-mono uppercase tracking-wider">DRM Compatible</span>
        </div>
      )
    }
    
    if (fileValidation && !fileValidation.isValid) {
      return (
        <div className="flex items-center text-danger-red">
          <AlertCircle className="h-4 w-4 mr-2" />
          <span className="text-sm font-mono uppercase tracking-wider">DRM Incompatible</span>
        </div>
      )
    }
    
    return (
      <div className="flex items-center text-stellar-cyan">
        <div className="h-4 w-4 border-2 border-stellar-cyan/50 rounded-full animate-spin mr-2"></div>
        <span className="text-sm font-mono uppercase tracking-wider">Validating...</span>
      </div>
    )
  }


  return (
    <motion.div 
      className="space-y-6"
      variants={gameUIVariants}
      initial="pageEnter"
      animate="pageVisible"
    >
      {/* Header */}
      <motion.div 
        className="text-center"
        variants={gameUIVariants}
        initial="elementEnter"
        animate="elementVisible"
      >
        <h1 className="text-3xl font-display font-bold text-white mb-1">
          Creator Upload
        </h1>
        <p className="text-stellar-cyan font-mono uppercase tracking-wider text-sm">
          Share your creativity with advanced DRM protection
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Area */}
        <div className="lg:col-span-2">
          <CosmicCard variant="data" size="lg">
            {/* File Drop Zone - Only show when no file is selected */}
            {!selectedFile && (
              <div {...getRootProps()}>
                <motion.div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 ${
                    isDragActive
                      ? 'border-stellar-blue bg-stellar-blue/10'
                      : 'border-stellar-cyan/30 hover:border-stellar-blue/60'
                  }`}
                  variants={gameUIVariants}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                <input {...getInputProps()} />
                <motion.div
                  animate={{ 
                    y: isDragActive ? -5 : 0,
                    scale: isDragActive ? 1.1 : 1
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <UploadIcon className="mx-auto h-12 w-12 text-stellar-blue mb-4" />
                </motion.div>
                <p className="text-lg font-display font-semibold text-white mb-2">
                  {isDragActive ? 'Drop your creation here' : 'Share your creativity'}
                </p>
                <p className="text-stellar-cyan font-mono mb-3 text-sm">
                  Drag & drop or click to select files
                </p>
                <p className="text-xs text-stellar-cyan/60 font-mono mb-3">
                  Max size: {formatFileSize(100 * 1024 * 1024)} • Supports: Images, Videos, Audio
                </p>
                <div className="flex items-center justify-center text-xs text-stellar-blue font-mono">
                  <Lock className="h-3 w-3 mr-2" />
                  <span>All uploads are protected with DRM</span>
                </div>
              </motion.div>
              </div>
            )}

            {/* Selected File */}
            {selectedFile && (
              <motion.div 
                className="mt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <CosmicCard variant="data" size="md">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {(() => {
                        const Icon = getFileIcon(selectedFile)
                        return <Icon className="h-8 w-8 text-stellar-blue mr-4" />
                      })()}
                      <div className="flex-1">
                        <p className="text-sm font-display font-semibold text-white">{selectedFile.name}</p>
                        <p className="text-xs text-stellar-cyan font-mono">
                          {getFileType(selectedFile)} • {formatFileSize(selectedFile.size)}
                        </p>
                        {getDRMStatus()}
                        {compressionInfo && (
                          <div className="mt-2">
                            <div className="flex items-center space-x-2">
                              {compressionInfo.willCompress ? (
                                <>
                                  <Zap className="h-3 w-3 text-warning-amber" />
                                  <span className="text-xs text-warning-amber">
                                    Will compress (~{compressionInfo.estimatedSavings}% savings)
                                  </span>
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="h-3 w-3 text-stellar-cyan/60" />
                                  <span className="text-xs text-stellar-cyan/60">
                                    Not suitable for compression
                                  </span>
                                </>
                              )}
                            </div>
                            {compressionInfo.willCompress && (
                              <div className="mt-1 text-xs text-stellar-cyan/60 font-mono">
                                Quality: {compressionInfo.quality} • Est. final size: {formatFileSize(selectedFile.size * (1 - compressionInfo.estimatedSavings / 100))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {fileValidation?.isValid ? (
                        <CheckCircle className="h-5 w-5 text-success-emerald" />
                      ) : fileValidation ? (
                        <AlertCircle className="h-5 w-5 text-danger-red" />
                      ) : (
                        <div className="h-5 w-5 border-2 border-stellar-cyan/50 rounded-full animate-spin"></div>
                      )}
                      <button
                        onClick={handleChangeFile}
                        className="p-2 text-stellar-cyan hover:text-white hover:bg-stellar-cyan/10 rounded-lg transition-colors"
                        title="Change file"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={resetForm}
                        className="p-2 text-danger-red hover:text-white hover:bg-danger-red/10 rounded-lg transition-colors"
                        title="Cancel and reset"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Media Preview */}
                  <div className="mt-4">
                    <h4 className="text-sm font-mono text-stellar-cyan mb-2 uppercase tracking-wider">Preview</h4>
                    <MediaPreview 
                      file={selectedFile} 
                      maxHeight={150}
                      className="w-full"
                    />
                  </div>
                </CosmicCard>
              </motion.div>
            )}

            {/* Upload Options */}
            {selectedFile && (
              <motion.div 
                className="mt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <CosmicCard variant="data" size="lg">
                  <h3 className="text-lg font-display font-semibold text-white mb-4">Creation Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CosmicInput
                      label="Title"
                      value={uploadOptions.title}
                      onChange={(value) => setUploadOptions(prev => ({ ...prev, title: value }))}
                      placeholder="Give your creation a title"
                    />
                    <div>
                      <label className="block text-sm font-mono text-stellar-cyan mb-2 uppercase tracking-wider">
                        Category
                      </label>
                      <select
                        value={uploadOptions.category}
                        onChange={(e) => setUploadOptions(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-3 bg-cosmic-dark/80 backdrop-blur-md border-2 border-stellar-cyan/30 rounded-lg text-white font-mono focus:border-stellar-blue focus:outline-none transition-all duration-300"
                      >
                        <option value="">Select category</option>
                        <option value="music">Music</option>
                        <option value="video">Video</option>
                        <option value="image">Image</option>
                        <option value="document">Document</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <CosmicTextarea
                        label="Description"
                        value={uploadOptions.description}
                        onChange={(value) => setUploadOptions(prev => ({ ...prev, description: value }))}
                        placeholder="Tell the story behind your creation"
                        rows={2}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <CosmicInput
                        label="Tags"
                        value={uploadOptions.tags.join(', ')}
                        onChange={(value) => setUploadOptions(prev => ({ 
                          ...prev, 
                          tags: value.split(',').map(tag => tag.trim()).filter(Boolean)
                        }))}
                        placeholder="Add tags to help others discover your work"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="flex items-center cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={uploadOptions.isPublic}
                          onChange={(e) => setUploadOptions(prev => ({ ...prev, isPublic: e.target.checked }))}
                          className="mr-3 w-4 h-4 text-stellar-blue bg-cosmic-dark border-stellar-cyan/30 rounded focus:ring-stellar-blue focus:ring-2"
                        />
                        <span className="text-sm font-mono text-stellar-cyan flex items-center group-hover:text-white transition-colors">
                          {uploadOptions.isPublic ? <Globe className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                          Make public for discovery
                        </span>
                      </label>
                    </div>
                  </div>
                </CosmicCard>
              </motion.div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <motion.div 
                className="mt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <CosmicCard variant="data" size="md">
                  <div className="space-y-4">
                    {/* Progress Header */}
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-mono text-stellar-cyan uppercase tracking-wider">
                        Upload Progress
                      </h4>
                      <span className="text-sm font-mono text-white">
                        {uploadProgress}%
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="relative">
                      <div className="w-full bg-cosmic-dark/50 rounded-full h-2 border border-stellar-cyan/30">
                        <motion.div
                          className="bg-gradient-to-r from-stellar-blue to-stellar-cyan h-2 rounded-full relative overflow-hidden"
                          initial={{ width: 0 }}
                          animate={{ width: `${uploadProgress}%` }}
                          transition={{ duration: 0.5 }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
                        </motion.div>
                      </div>
                    </div>

                    {/* Stage Information */}
                    <div className="flex items-center space-x-3">
                      <div className="h-2 w-2 bg-stellar-blue rounded-full animate-pulse" />
                      <span className="text-sm text-stellar-cyan font-mono">
                        {uploadStage}
                      </span>
                    </div>

                    {/* Progress Stages */}
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div className={`flex flex-col items-center p-2 rounded ${uploadProgress >= 30 ? 'text-success-emerald' : 'text-stellar-cyan/60'}`}>
                        <Zap className="h-4 w-4 mb-1" />
                        <span className="font-mono">Compress</span>
                      </div>
                      <div className={`flex flex-col items-center p-2 rounded ${uploadProgress >= 60 ? 'text-success-emerald' : 'text-stellar-cyan/60'}`}>
                        <Shield className="h-4 w-4 mb-1" />
                        <span className="font-mono">Protect</span>
                      </div>
                      <div className={`flex flex-col items-center p-2 rounded ${uploadProgress >= 90 ? 'text-success-emerald' : 'text-stellar-cyan/60'}`}>
                        <Globe className="h-4 w-4 mb-1" />
                        <span className="font-mono">Upload</span>
                      </div>
                      <div className={`flex flex-col items-center p-2 rounded ${uploadProgress >= 100 ? 'text-success-emerald' : 'text-stellar-cyan/60'}`}>
                        <CheckCircle className="h-4 w-4 mb-1" />
                        <span className="font-mono">Complete</span>
                      </div>
                    </div>
                  </div>
                </CosmicCard>
              </motion.div>
            )}

            {/* Upload Button */}
            {selectedFile && (
              <motion.div 
                className="mt-4 flex justify-end"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <CosmicButton
                  onClick={handleUpload}
                  disabled={uploading || !fileValidation?.isValid}
                  loading={uploading}
                  icon={<Shield className="h-4 w-4" />}
                  size="lg"
                >
                  {uploading ? 'Processing...' : 
                   !fileValidation?.isValid ? 'Waiting for validation...' : 'Upload with DRM Protection'}
                </CosmicButton>
              </motion.div>
            )}
          </CosmicCard>
        </div>

        {/* Creator Features Info */}
        <div className="lg:col-span-1">
          <CosmicCard variant="data" size="lg">
            <h3 className="text-lg font-display font-semibold text-white mb-4">Platform Features</h3>
            <div className="space-y-4">
              <motion.div 
                className="flex items-start"
                variants={gameUIVariants}
                initial="elementEnter"
                animate="elementVisible"
                transition={{ delay: 0.1 }}
              >
                <Shield className="h-5 w-5 text-stellar-blue mr-3 mt-1" />
                <div>
                  <p className="text-sm font-display font-semibold text-white">Advanced DRM</p>
                  <p className="text-xs text-stellar-cyan font-mono">SHA-256 file fingerprinting</p>
                </div>
              </motion.div>
              <motion.div 
                className="flex items-start"
                variants={gameUIVariants}
                initial="elementEnter"
                animate="elementVisible"
                transition={{ delay: 0.2 }}
              >
                <Lock className="h-5 w-5 text-success-emerald mr-3 mt-1" />
                <div>
                  <p className="text-sm font-display font-semibold text-white">Content Protection</p>
                  <p className="text-xs text-stellar-cyan font-mono">Steganographic watermarking</p>
                </div>
              </motion.div>
              <motion.div 
                className="flex items-start"
                variants={gameUIVariants}
                initial="elementEnter"
                animate="elementVisible"
                transition={{ delay: 0.3 }}
              >
                <Zap className="h-5 w-5 text-warning-amber mr-3 mt-1" />
                <div>
                  <p className="text-sm font-display font-semibold text-white">Smart Compression</p>
                  <p className="text-xs text-stellar-cyan font-mono">Reduce storage costs by 20-50%</p>
                </div>
              </motion.div>
              <motion.div 
                className="flex items-start"
                variants={gameUIVariants}
                initial="elementEnter"
                animate="elementVisible"
                transition={{ delay: 0.4 }}
              >
                <Star className="h-5 w-5 text-nebula-purple mr-3 mt-1" />
                <div>
                  <p className="text-sm font-display font-semibold text-white">NFT Ready</p>
                  <p className="text-xs text-stellar-cyan font-mono">Mint as NFT anytime</p>
                </div>
              </motion.div>
              <motion.div 
                className="flex items-start"
                variants={gameUIVariants}
                initial="elementEnter"
                animate="elementVisible"
                transition={{ delay: 0.5 }}
              >
                <Globe className="h-5 w-5 text-solar-gold mr-3 mt-1" />
                <div>
                  <p className="text-sm font-display font-semibold text-white">Analytics</p>
                  <p className="text-xs text-stellar-cyan font-mono">Track your audience</p>
                </div>
              </motion.div>
            </div>

            {/* Supported Formats */}
            <div className="mt-6">
              <h4 className="text-sm font-display font-semibold text-white mb-2">Supported Formats</h4>
              <div className="text-xs text-stellar-cyan font-mono space-y-1">
                <p>• Video: MP4, MKV, AVI, MOV, WMV</p>
                <p>• Audio: MP3, WAV, AAC, M4A, FLAC</p>
                <p>• Images: JPEG, PNG, GIF, WebP</p>
              </div>
            </div>
          </CosmicCard>
        </div>
      </div>
    </motion.div>
  )
}

export default Upload 